import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Lock, FolderOpen, User, Mail, Camera, Loader2, CheckCircle2, Percent, Trash2, KeyRound, Plus, Eye, EyeOff, ExternalLink, Copy, Save, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import api, { formatApiErrorDetail, uploadFileUrl, resolveProfileImage } from "@/lib/api";
import Modal from "./Modal";

// Neutral silhouette (no user identity). Used only if a logged-in user has no
// profile picture uploaded yet — never leak another user's photo as a fallback.
const DEFAULT_PHOTO =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='%23E5E7EB'/><circle cx='50' cy='38' r='18' fill='%2394A3B8'/><path d='M14 92c0-20 16-32 36-32s36 12 36 32' fill='%2394A3B8'/></svg>";

export default function HamburgerMenu() {
  const [open, setOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [vaultOpen, setVaultOpen] = useState(false);
  const { user, logout, refresh } = useAuth();
  const isAdmin =
    user?.role === "admin" ||
    (user?.email && user.email.toLowerCase().trim() === "daniel@hassein.com");
  const photo = user?.profile_image
    ? resolveProfileImage(user.profile_image)
    : (user?.profile_photo ? uploadFileUrl(user.profile_photo) : DEFAULT_PHOTO);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-11 h-11 rounded-2xl flex items-center justify-center border-2 hover:bg-slate-50 transition"
        style={{ borderColor: "#C9C5B0", background: "#fff" }}
        data-testid="hamburger-btn"
        aria-label="תפריט"
      >
        <Menu size={20} className="text-slate-700" />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            onClick={() => setOpen(false)}
            data-testid="menu-backdrop"
          />
          <aside
            className="fixed top-0 bottom-0 z-50 shadow-2xl overflow-y-auto"
            style={{
              insetInlineStart: 0,
              width: "min(340px, 90vw)",
              background: "#FBFAF3",
              borderInlineEnd: "2px solid #C9C5B0",
              animation: "slideInStart 220ms cubic-bezier(0.16,1,0.3,1)",
            }}
            data-testid="menu-drawer"
          >
            <style>{`@keyframes slideInStart { from { transform: translateX(-100%); } to { transform: translateX(0); } }`}</style>
            <div
              className="p-5 flex items-center justify-between border-b-2"
              style={{ borderColor: "#C9C5B0" }}
            >
              <div className="flex items-center gap-3">
                <img
                  src={photo}
                  alt={user?.name || "user"}
                  className="w-11 h-11 rounded-full object-cover border-2 border-white shadow"
                />
                <div>
                  <div className="font-heebo font-bold text-slate-900">
                    {user?.name || "משתמש"}
                  </div>
                  <div className="text-xs text-slate-500" dir="ltr">
                    {user?.email}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="btn-ghost"
                data-testid="menu-close"
                aria-label="סגור"
              >
                <X size={18} />
              </button>
            </div>

            <nav className="p-3 space-y-1">
              <MenuItem
                icon={<Lock size={16} />}
                iconBg="#FEF3C7"
                iconColor="#92400E"
                title="ניהול סיסמה"
                subtitle="שנה את סיסמת הכניסה"
                onClick={() => {
                  setOpen(false);
                  setPwdOpen(true);
                }}
                testid="menu-change-password"
              />
              <MenuItem
                icon={<KeyRound size={16} />}
                iconBg="#DBEAFE"
                iconColor="#1E40AF"
                title="ניהול קודים וסיסמאות"
                subtitle="כספת אישית לסיסמאות של אתרים ושירותים"
                onClick={() => {
                  setOpen(false);
                  setVaultOpen(true);
                }}
                testid="menu-vault"
              />
              <MenuItem
                icon={<Mail size={16} />}
                iconBg="#E0E7FF"
                iconColor="#3730A3"
                title="שנה מייל כניסה"
                subtitle={user?.email}
                onClick={() => {
                  setOpen(false);
                  setEmailOpen(true);
                }}
                testid="menu-change-email"
              />
              <MenuItem
                icon={<Camera size={16} />}
                iconBg="#FCE7F3"
                iconColor="#9D174D"
                title="שנה תמונת פרופיל"
                subtitle="העלה תמונה חדשה"
                onClick={() => {
                  setOpen(false);
                  setPhotoOpen(true);
                }}
                testid="menu-change-photo"
              />
              <MenuItem
                icon={<Percent size={16} />}
                iconBg="#EDE9FE"
                iconColor="#5B21B6"
                title="אחוזי מס"
                subtitle="מע״מ · מס הכנסה · ביטוח לאומי"
                onClick={() => {
                  setOpen(false);
                  setTaxOpen(true);
                }}
                testid="menu-tax-settings"
              />
              <Link
                to="/app/files"
                className="menu-item"
                onClick={() => setOpen(false)}
                data-testid="menu-all-files"
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: "#E0F2FE", color: "#075985" }}
                >
                  <FolderOpen size={16} />
                </div>
                <div className="text-start">
                  <div className="font-heebo font-semibold text-slate-800">
                    כל הקבצים
                  </div>
                  <div className="text-xs text-slate-500">
                    חשבוניות וקבלות שהעלית
                  </div>
                </div>
              </Link>
              {isAdmin && (
                <MenuItem
                  icon={<Trash2 size={16} />}
                  iconBg="#FEE2E2"
                  iconColor="#B91C1C"
                  title="מחק משתמש"
                  subtitle="ניהול חשבונות משתמשים"
                  onClick={() => {
                    setOpen(false);
                    setUsersOpen(true);
                  }}
                  testid="menu-delete-user"
                />
              )}
            </nav>

            <StorageStatsPanel />

            <div className="p-3 border-t mt-4" style={{ borderColor: "#E5E1D0" }}>
              <button
                className="btn-ghost w-full justify-center text-xs text-slate-500"
                onClick={async () => {
                  await logout();
                  setOpen(false);
                }}
                data-testid="menu-logout"
              >
                יציאה מהחשבון
              </button>
            </div>

            <style>{`
              .menu-item {
                width: 100%;
                display: flex;
                align-items: center;
                gap: 0.75rem;
                padding: 0.7rem 0.85rem;
                border-radius: 1rem;
                transition: background 160ms ease;
                cursor: pointer;
                background: transparent;
                border: none;
                text-align: right;
              }
              .menu-item:hover { background: rgba(44,94,78,0.06); }
            `}</style>
          </aside>
        </>
      )}

      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
      <ChangeEmailModal open={emailOpen} onClose={() => setEmailOpen(false)} onSuccess={refresh} />
      <ChangePhotoModal open={photoOpen} onClose={() => setPhotoOpen(false)} onSuccess={refresh} />
      <TaxSettingsModal open={taxOpen} onClose={() => setTaxOpen(false)} />
      <DeleteUserModal open={usersOpen} onClose={() => setUsersOpen(false)} />
      <VaultModal open={vaultOpen} onClose={() => setVaultOpen(false)} />
    </>
  );
}

function TaxSettingsModal({ open, onClose }) {
  const { taxRates, save, reload } = useSettings();
  const [vat, setVat] = useState(String(taxRates.vat));
  const [itax, setItax] = useState(String(taxRates.income_tax));
  const [bl, setBl] = useState(String(taxRates.social_security));
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (open) {
      setVat(String(taxRates.vat));
      setItax(String(taxRates.income_tax));
      setBl(String(taxRates.social_security));
    }
  }, [open, taxRates]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await save({
        vat: parseFloat(vat) || 0,
        income_tax: parseFloat(itax) || 0,
        social_security: parseFloat(bl) || 0,
      });
      await reload();
      setFlash(true);
      setTimeout(() => {
        setFlash(false);
        onClose();
      }, 900);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="אחוזי מס" testId="tax-settings-modal">
      <form onSubmit={submit} className="p-4 space-y-4">
        <div className="text-sm text-slate-600 font-heebo">
          שנה את אחוזי המס הרלוונטיים לחשבונך. השינוי יחול מיד על כל החישובים במערכת.
        </div>
        <PctField label="מע״מ (%)" value={vat} onChange={setVat} testid="tax-vat-input" bg="#EDE9FE" text="#5B21B6" />
        <PctField label="מס הכנסה (%)" value={itax} onChange={setItax} testid="tax-income-input" bg="#FCE7F3" text="#9D174D" />
        <PctField label="ביטוח לאומי (%)" value={bl} onChange={setBl} testid="tax-bl-input" bg="#FEE2E2" text="#991B1B" />
        <button
          type="submit"
          className="btn-primary w-full justify-center"
          disabled={saving}
          data-testid="submit-tax-settings"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : flash ? <CheckCircle2 size={16} /> : null}
          {flash ? "נשמר!" : "שמור"}
        </button>
      </form>
    </Modal>
  );
}

function PctField({ label, value, onChange, testid, bg, text }) {
  return (
    <div className="rounded-2xl p-3 border" style={{ background: bg, borderColor: text + "33" }}>
      <label className="text-xs font-heebo font-semibold block mb-1" style={{ color: text }}>
        {label}
      </label>
      <input
        type="number"
        step="0.01"
        min="0"
        max="100"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white rounded-lg px-3 py-2 text-lg num-pop border-2 focus:outline-none focus:border-slate-400"
        style={{ borderColor: text + "22", color: text }}
        data-testid={testid}
      />
    </div>
  );
}

function StorageStatsPanel() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const res = await api.get("/storage/stats");
        if (ok) setStats(res.data);
      } catch { /* noop */ }
    })();
    return () => { ok = false; };
  }, []);
  if (!stats) return null;
  const fmtBytes = (b) => {
    if (b >= 1024 * 1024 * 1024) return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
    if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    if (b >= 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${b} B`;
  };
  const fmtQuota = (mb) => (mb >= 1024 ? `${(mb / 1024).toFixed(0)} GB` : `${mb} MB`);
  const nearLimit = stats.used_percent >= 80;
  const unassignedColor = "#94A3B8";
  const rows = [
    ...stats.breakdown.map(b => ({ key: b.user_id, label: b.is_me ? "אתה" : b.full_name, bytes: b.bytes, color: b.color, bold: b.is_me })),
  ];
  if (stats.unassigned_bytes > 0) {
    rows.push({ key: "__unassigned__", label: "קבצים כלליים", bytes: stats.unassigned_bytes, color: unassignedColor, bold: false });
  }
  return (
    <div className="mx-4 mb-4 mt-2 rounded-2xl border-2 p-4" style={{
      background: nearLimit ? "linear-gradient(135deg, #FEE2E2, #FEF3C7)" : "linear-gradient(135deg, #F0FDF4, #DBEAFE)",
      borderColor: nearLimit ? "#F87171" : "#93C5FD"
    }} data-testid="storage-stats">
      <div className="flex items-center justify-between mb-2">
        <div className="font-heebo font-bold text-slate-800 text-sm">זכרון מערכת</div>
        <div className="text-xs font-heebo text-slate-600 num-pop" dir="ltr">
          {fmtQuota(stats.quota_mb)} / {fmtBytes(stats.used_bytes)}
        </div>
      </div>
      <div className="h-3 rounded-full bg-white/60 overflow-hidden flex" dir="ltr" data-testid="storage-bar">
        {rows.filter(r => r.bytes > 0).map(r => {
          const pct = (r.bytes / stats.quota_bytes) * 100;
          return <div key={r.key} style={{ width: `${pct}%`, background: r.color }} title={r.label} />;
        })}
      </div>
      <div className="mt-3 space-y-1">
        {rows.map(r => (
          <div key={r.key} className="flex items-center justify-between text-xs font-heebo" data-testid={`storage-row-${r.key}`}>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />
              <span className={r.bold ? "font-bold text-slate-900" : "text-slate-600"}>
                {r.label}
              </span>
            </div>
            <span className="num-pop text-slate-700" dir="ltr">{fmtBytes(r.bytes)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between text-xs font-heebo pt-1 mt-1 border-t border-white/60" data-testid="storage-total">
          <span className="font-bold text-slate-900">סה״כ</span>
          <span className="num-pop font-bold text-slate-900" dir="ltr">{fmtBytes(stats.used_bytes)}</span>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-white/50 flex items-center justify-between">
        <div className="text-xs font-heebo text-slate-700">
          <span className="num-pop text-lg">{stats.used_percent}%</span> נוצל
        </div>
      </div>
      {nearLimit && (
        <div className="mt-2 text-[11px] font-heebo text-red-800">
          ⚠️ מתקרב לגבול. שקול לשדרג או למחוק קבצים ישנים.
        </div>
      )}
      <button
        type="button"
        className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl py-2 px-3 font-heebo font-bold text-white text-xs shadow-md hover:shadow-lg transition transform hover:-translate-y-0.5"
        style={{ background: "linear-gradient(135deg, #10B981 0%, #059669 100%)" }}
        onClick={() => window.open("mailto:support@hassein.dev?subject=שדרוג לאחסון 1 GB", "_blank")}
        data-testid="upgrade-1gb-btn"
      >
        <span>שדרג לאחסון של 1 GB ב-19 ₪ בחודש</span>
      </button>
      <button
        type="button"
        className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl py-2 px-3 font-heebo font-bold text-white text-xs shadow-md hover:shadow-lg transition transform hover:-translate-y-0.5"
        style={{ background: "linear-gradient(135deg, #7C3AED 0%, #4338CA 100%)" }}
        onClick={() => window.open("mailto:support@hassein.dev?subject=שדרוג לאחסון 2 GB", "_blank")}
        data-testid="upgrade-2gb-btn"
      >
        <span>שדרג לאחסון של 2 GB ב-29 ₪ בחודש</span>
      </button>
    </div>
  );
}

function MenuItem({ icon, iconBg, iconColor, title, subtitle, onClick, testid }) {
  return (
    <button className="menu-item" onClick={onClick} data-testid={testid}>
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center"
        style={{ background: iconBg, color: iconColor }}
      >
        {icon}
      </div>
      <div className="text-start flex-1">
        <div className="font-heebo font-semibold text-slate-800">{title}</div>
        {subtitle && (
          <div className="text-xs text-slate-500 truncate max-w-[200px]" dir="auto">
            {subtitle}
          </div>
        )}
      </div>
    </button>
  );
}

function ChangePasswordModal({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrent("");
      setNext("");
      setConfirm("");
      setErr("");
      setOk(false);
    }
  }, [open]);

  const submit = async () => {
    setErr("");
    setOk(false);
    if (next.length < 4) return setErr("סיסמה חדשה חייבת להיות בת 4 תווים לפחות");
    if (next !== confirm) return setErr("הסיסמאות אינן תואמות");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setOk(true);
      setTimeout(onClose, 1200);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="ניהול סיסמה"
      testId="change-password-modal"
      footer={
        <>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy}
            data-testid="submit-change-password"
          >
            {busy ? "שומר…" : "שמור סיסמה חדשה"}
          </button>
          <button className="btn-secondary" onClick={onClose}>
            ביטול
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <FieldPw label="סיסמה נוכחית" value={current} onChange={setCurrent} testid="current-password-input" />
        <FieldPw label="סיסמה חדשה" value={next} onChange={setNext} testid="new-password-input" />
        <FieldPw label="אימות סיסמה חדשה" value={confirm} onChange={setConfirm} testid="confirm-password-input" />
        {err && <div className="chip chip-danger">{err}</div>}
        {ok && (
          <div className="chip" style={{ background: "#DCFCE7", color: "#166534" }}>
            הסיסמה עודכנה בהצלחה
          </div>
        )}
      </div>
    </Modal>
  );
}

function ChangeEmailModal({ open, onClose, onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setPassword("");
      setErr("");
      setOk(false);
    }
  }, [open]);

  const submit = async () => {
    setErr("");
    setOk(false);
    if (!email) return setErr("יש להזין אימייל חדש");
    setBusy(true);
    try {
      await api.post("/auth/change-email", {
        current_password: password,
        new_email: email,
      });
      setOk(true);
      await onSuccess?.();
      setTimeout(onClose, 1200);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="שינוי מייל כניסה"
      testId="change-email-modal"
      footer={
        <>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy}
            data-testid="submit-change-email"
          >
            {busy ? (
              <>
                <Loader2 size={14} className="animate-spin" /> שומר…
              </>
            ) : (
              "שמור מייל חדש"
            )}
          </button>
          <button className="btn-secondary" onClick={onClose}>
            ביטול
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <div className="text-xs text-slate-500 mb-1 font-heebo">אימייל חדש</div>
          <input
            type="email"
            className="input-field"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="new-email-input"
            dir="ltr"
            placeholder="new@email.com"
          />
        </div>
        <FieldPw
          label="סיסמה נוכחית (לאימות)"
          value={password}
          onChange={setPassword}
          testid="email-current-password-input"
        />
        {err && <div className="chip chip-danger">{err}</div>}
        {ok && (
          <div className="chip" style={{ background: "#DCFCE7", color: "#166534" }}>
            האימייל עודכן בהצלחה
          </div>
        )}
        <div className="text-xs text-slate-500 font-heebo">
          לאחר השמירה, המייל החדש ישמש לכניסה.
        </div>
      </div>
    </Modal>
  );
}

function ChangePhotoModal({ open, onClose, onSuccess }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setFile(null);
      setErr("");
      setOk(false);
    }
  }, [open]);

  const onFile = (f) => {
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    setErr("");
    setOk(false);
    if (!file) return setErr("יש לבחור תמונה");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/auth/profile-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOk(true);
      await onSuccess?.();
      setTimeout(onClose, 1000);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const currentPhoto = user?.profile_image
    ? resolveProfileImage(user.profile_image)
    : (user?.profile_photo ? uploadFileUrl(user.profile_photo) : DEFAULT_PHOTO);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="שינוי תמונת פרופיל"
      testId="change-photo-modal"
      footer={
        <>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !file}
            data-testid="submit-change-photo"
          >
            {busy ? (
              <>
                <Loader2 size={14} className="animate-spin" /> מעלה…
              </>
            ) : (
              "שמור תמונה"
            )}
          </button>
          <button className="btn-secondary" onClick={onClose}>
            ביטול
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex flex-col items-center gap-2">
            <div className="text-xs text-slate-500 font-heebo">נוכחי</div>
            <img
              src={currentPhoto}
              alt="נוכחי"
              className="w-28 h-28 rounded-full object-cover border-4 border-white shadow ring-2 ring-slate-200"
            />
          </div>
          <div className="text-slate-300 text-3xl">←</div>
          <div className="flex flex-col items-center gap-2">
            <div className="text-xs text-slate-500 font-heebo">חדש</div>
            <div
              className="w-28 h-28 rounded-full border-4 border-dashed flex items-center justify-center overflow-hidden"
              style={{
                borderColor: preview ? "#22C55E" : "#98CBB4",
                background: preview ? "#F0FDF4" : "#F0F7F2",
              }}
            >
              {preview ? (
                <img
                  src={preview}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Camera size={26} className="text-emerald-700" />
              )}
            </div>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.heic"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="photo-file-input"
        />
        <button
          type="button"
          className="btn-secondary w-full justify-center"
          onClick={() => fileRef.current?.click()}
          data-testid="pick-photo-btn"
        >
          <Camera size={14} /> בחר תמונה (JPG/PNG/WEBP)
        </button>
        {err && <div className="chip chip-danger">{err}</div>}
        {ok && (
          <div className="chip" style={{ background: "#DCFCE7", color: "#166534" }}>
            <CheckCircle2 size={12} /> התמונה עודכנה
          </div>
        )}
      </div>
    </Modal>
  );
}

function FieldPw({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-xs text-slate-500 mb-1 font-heebo">{label}</div>
      <input
        type="text"
        className="input-field pw-masked"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        dir="ltr"
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        data-form-type="other"
        name={`pw-${testid || "field"}`}
        role="textbox"
        aria-label={label}
      />
    </div>
  );
}

function DeleteUserModal({ open, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data.users || []);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || "טעינת משתמשים נכשלה");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    else {
      setConfirmId(null);
      setErr("");
    }
  }, [open]);

  const doDelete = async (targetId) => {
    setDeleting(true);
    setErr("");
    try {
      await api.delete(`/admin/users/${targetId}`);
      setUsers(prev => prev.filter(u => u.id !== targetId));
      setConfirmId(null);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || "מחיקה נכשלה");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="מחק משתמש" testId="delete-user-modal">
      <div className="space-y-3" data-testid="delete-user-list">
        <div className="text-xs font-heebo text-slate-500 mb-2">
          מחיקת משתמש תסיר לצמיתות את כל הנתונים שלו: הכנסות, הוצאות, חסכונות וקבצים. אין דרך חזרה.
        </div>

        {loading && (
          <div className="flex items-center justify-center py-6 text-slate-500">
            <Loader2 size={20} className="animate-spin" />
          </div>
        )}

        {!loading && err && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl p-2 font-heebo">
            {err}
          </div>
        )}

        {!loading && !err && users.length === 0 && (
          <div className="text-sm text-slate-500 font-heebo text-center py-4">
            אין משתמשים נוספים במערכת
          </div>
        )}

        {!loading && users.map(u => {
          const photo = resolveProfileImage(u.profile_image) || (u.profile_photo ? uploadFileUrl(u.profile_photo) : "");
          const disabled = u.is_admin || u.is_me;
          const isConfirming = confirmId === u.id;
          return (
            <div
              key={u.id}
              className="flex items-center gap-3 p-3 rounded-2xl border"
              style={{ background: disabled ? "#F8FAFC" : "#FFFFFF", borderColor: "#E5E7EB" }}
              data-testid={`delete-user-row-${u.id}`}
            >
              {photo ? (
                <img src={photo} alt={u.full_name} className="w-10 h-10 rounded-full object-cover bg-slate-100" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: "var(--brand)" }}>
                  {u.full_name?.[0] || "?"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-heebo font-semibold text-slate-900 text-sm truncate">
                  {u.full_name}
                  {u.is_admin && <span className="mr-2 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">מנהל</span>}
                  {u.is_me && !u.is_admin && <span className="mr-2 text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">אתה</span>}
                </div>
                <div className="text-xs text-slate-500 truncate" dir="ltr">{u.email}</div>
              </div>

              {disabled ? (
                <div className="text-[11px] text-slate-400 font-heebo px-2">לא ניתן למחיקה</div>
              ) : isConfirming ? (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="text-xs font-heebo font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg px-3 py-1.5 disabled:opacity-50"
                    onClick={() => doDelete(u.id)}
                    disabled={deleting}
                    data-testid={`delete-user-confirm-${u.id}`}
                  >
                    {deleting ? <Loader2 size={12} className="animate-spin" /> : "מחק"}
                  </button>
                  <button
                    type="button"
                    className="text-xs font-heebo text-slate-500 hover:text-slate-900 rounded-lg px-2 py-1.5"
                    onClick={() => setConfirmId(null)}
                    disabled={deleting}
                  >
                    ביטול
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition"
                  onClick={() => setConfirmId(u.id)}
                  data-testid={`delete-user-btn-${u.id}`}
                  title="מחק משתמש זה"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}


function VaultModal({ open, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [revealed, setRevealed] = useState({}); // id -> bool
  const [copied, setCopied] = useState("");

  // form state
  const [form, setForm] = useState({ name: "", url: "", notes: "", password: "" });

  const resetForm = () => setForm({ name: "", url: "", notes: "", password: "" });

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/vault/entries");
      setEntries(res.data.entries || []);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || "טעינת רשומות נכשלה");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      load();
    } else {
      setAdding(false);
      setEditingId(null);
      setConfirmDeleteId(null);
      setRevealed({});
      resetForm();
      setErr("");
    }
  }, [open]);

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setAdding(false);
    setForm({
      name: entry.name || "",
      url: entry.url || "",
      notes: entry.notes || "",
      password: entry.password || "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || !form.password) {
      setErr("שם וסיסמה הם שדות חובה");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/vault/entries/${editingId}`, form);
      } else {
        await api.post("/vault/entries", form);
      }
      await load();
      setAdding(false);
      setEditingId(null);
      resetForm();
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || "שמירה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/vault/entries/${id}`);
      setEntries(prev => prev.filter(e => e.id !== id));
      setConfirmDeleteId(null);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || "מחיקה נכשלה");
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* noop */ }
  };

  const showForm = adding || editingId;

  return (
    <Modal open={open} onClose={onClose} title="ניהול קודים וסיסמאות" testId="vault-modal">
      <div className="space-y-3" data-testid="vault-list">
        {!showForm && (
          <button
            type="button"
            className="w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-3 font-heebo font-semibold text-sm hover:bg-slate-50 transition"
            style={{ borderColor: "#93C5FD", color: "#1E40AF" }}
            onClick={() => { setAdding(true); resetForm(); }}
            data-testid="vault-add-btn"
          >
            <Plus size={16} /> הוסף סיסמה חדשה
          </button>
        )}

        {showForm && (
          <form onSubmit={submit} className="rounded-2xl border-2 p-4 space-y-3" style={{ borderColor: "#DBEAFE", background: "#F8FAFC" }} data-testid="vault-form">
            <div className="font-heebo font-bold text-sm text-slate-800">
              {editingId ? "עריכת רשומה" : "רשומה חדשה"}
            </div>
            <label className="block">
              <div className="text-xs text-slate-500 mb-1 font-heebo">שם *</div>
              <input
                type="text"
                className="input-field"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                required
                placeholder="למשל: Gmail, Facebook, בנק לאומי"
                data-testid="vault-name-input"
              />
            </label>
            <label className="block">
              <div className="text-xs text-slate-500 mb-1 font-heebo">לינק לאתר</div>
              <input
                type="url"
                className="input-field"
                value={form.url}
                onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
                placeholder="https://example.com"
                dir="ltr"
                data-testid="vault-url-input"
              />
            </label>
            <label className="block">
              <div className="text-xs text-slate-500 mb-1 font-heebo">פרטים / הערות</div>
              <textarea
                className="input-field min-h-[70px]"
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="שם משתמש, מייל, שאלת אבטחה..."
                data-testid="vault-notes-input"
              />
            </label>
            <label className="block">
              <div className="text-xs text-slate-500 mb-1 font-heebo">סיסמה *</div>
              <div className="relative">
                <input
                  type="text"
                  className={`input-field pr-12 ${revealed.__form ? "" : "pw-masked"}`}
                  value={form.password}
                  onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                  required
                  dir="ltr"
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                  name="pw-vault-form"
                  data-testid="vault-password-input"
                  style={{ paddingRight: "3rem" }}
                />
                <button
                  type="button"
                  onClick={() => setRevealed(r => ({ ...r, __form: !r.__form }))}
                  className="absolute inset-y-0 right-2 flex items-center px-2 text-slate-500 hover:text-slate-800 transition"
                  tabIndex={-1}
                  aria-label={revealed.__form ? "הסתר" : "הצג"}
                >
                  {revealed.__form ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>

            {err && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl p-2 font-heebo">
                {err}
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 text-white font-heebo font-bold py-2.5 hover:bg-slate-800 disabled:opacity-50 text-sm"
                data-testid="vault-save-btn"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                אישור ושמירה
              </button>
              <button
                type="button"
                onClick={() => { setAdding(false); setEditingId(null); resetForm(); setErr(""); }}
                className="rounded-xl border border-slate-300 text-slate-700 font-heebo font-semibold px-4 py-2.5 hover:bg-slate-100 text-sm"
                data-testid="vault-cancel-btn"
              >
                ביטול
              </button>
            </div>
          </form>
        )}

        {loading && !showForm && (
          <div className="flex items-center justify-center py-6 text-slate-500">
            <Loader2 size={20} className="animate-spin" />
          </div>
        )}

        {!loading && !showForm && err && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl p-2 font-heebo">
            {err}
          </div>
        )}

        {!loading && !showForm && entries.length === 0 && (
          <div className="text-sm text-slate-500 font-heebo text-center py-6" data-testid="vault-empty">
            עדיין לא הוספת סיסמאות.<br />הוסף את הראשונה כדי לשמור אותה בטוח כאן.
          </div>
        )}

        {!showForm && entries.map(entry => {
          const shown = !!revealed[entry.id];
          const isConfirming = confirmDeleteId === entry.id;
          return (
            <div key={entry.id} className="rounded-2xl border p-3 bg-white" style={{ borderColor: "#E5E7EB" }} data-testid={`vault-entry-${entry.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-heebo font-bold text-slate-900 text-sm truncate">{entry.name}</div>
                  {entry.url && (
                    <a
                      href={entry.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs inline-flex items-center gap-1 font-heebo hover:underline"
                      style={{ color: "#2563EB", direction: "ltr" }}
                      data-testid={`vault-entry-url-${entry.id}`}
                    >
                      <ExternalLink size={11} />
                      <span className="truncate max-w-[220px]">{entry.url}</span>
                    </a>
                  )}
                  {entry.notes && (
                    <div className="text-xs text-slate-600 font-heebo mt-1 whitespace-pre-wrap break-words">
                      {entry.notes}
                    </div>
                  )}
                </div>
                {isConfirming ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      className="text-xs font-heebo font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg px-2.5 py-1.5"
                      onClick={() => doDelete(entry.id)}
                      data-testid={`vault-delete-confirm-${entry.id}`}
                    >
                      מחק
                    </button>
                    <button
                      type="button"
                      className="text-xs font-heebo text-slate-500 hover:text-slate-900 rounded-lg px-2 py-1.5"
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      ביטול
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition"
                      onClick={() => startEdit(entry)}
                      title="ערוך"
                      data-testid={`vault-edit-${entry.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition"
                      onClick={() => setConfirmDeleteId(entry.id)}
                      title="מחק"
                      data-testid={`vault-delete-${entry.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-3 flex items-center gap-2 bg-slate-50 rounded-xl p-2 border border-slate-200">
                <div className="text-xs text-slate-500 font-heebo shrink-0">סיסמה:</div>
                <div className={`flex-1 font-mono text-sm text-slate-900 truncate ${shown ? "" : "tracking-widest"}`} dir="ltr" data-testid={`vault-password-${entry.id}`}>
                  {shown ? entry.password : "•".repeat(Math.min(entry.password?.length || 8, 12))}
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-white hover:text-slate-900 transition"
                  onClick={() => setRevealed(r => ({ ...r, [entry.id]: !r[entry.id] }))}
                  title={shown ? "הסתר" : "הצג"}
                  data-testid={`vault-toggle-visibility-${entry.id}`}
                >
                  {shown ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  type="button"
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-white hover:text-slate-900 transition"
                  onClick={() => copyToClipboard(entry.password, entry.id)}
                  title="העתק"
                  data-testid={`vault-copy-${entry.id}`}
                >
                  {copied === entry.id ? <CheckCircle2 size={14} className="text-green-600" /> : <Copy size={14} />}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
